import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform, useInView } from "framer-motion";
import { ChevronRight, Dumbbell, Zap, Target, ArrowRight, X, Users, User, Home, Trophy } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const FadeIn = ({ children, delay = 0, className = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 40 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.7, delay, ease: "easeOut" }} className={className}>
      {children}
    </motion.div>
  );
};

const SplashPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizStep, setQuizStep] = useState(0);
  const [scrolled, setScrolled] = useState(false);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroOpacity = useTransform(scrollYProgress, [0, 1], [1, 0]);
  const heroScale = useTransform(scrollYProgress, [0, 1], [1, 1.1]);

  useEffect(() => {
    if (user) {
      const isAdminOrCoach = user?.role === "admin" || user?.role === "coach";
      const sub = user?.subscription_tier;
      const hasActive = sub && sub !== "free" && sub !== "none" && sub !== "";
      if (isAdminOrCoach || hasActive) navigate("/dashboard");
    }
  }, [user, navigate]);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  return (
    <div className="min-h-screen bg-[#070B14] text-white">
      {/* Sticky Nav */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? "bg-[#070B14]/95 backdrop-blur-md border-b border-white/5" : "bg-transparent"}`}>
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img src="/paladin-logo.jpeg" alt="Paladin" className="w-9 h-9 rounded-lg object-contain" />
            <div>
              <span className="font-bold text-sm tracking-wider text-white block leading-none">PALADIN</span>
              <span className="text-[8px] text-white/40 tracking-[0.15em] uppercase">Health & Human Performance</span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <button onClick={() => navigate("/login")} className="text-white/50 hover:text-white text-sm transition-colors hidden sm:block">Sign In</button>
            <button onClick={() => setShowQuiz(true)} className="bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold px-5 py-2 rounded-lg transition-colors" data-testid="nav-find-program">
              Find Your Program
            </button>
          </div>
        </div>
      </nav>

      {/* Hero — Lead with empathy & tension */}
      <section ref={heroRef} className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <motion.div className="absolute inset-0 z-0" style={{ scale: heroScale }}>
          <img src="https://images.unsplash.com/photo-1770513649465-2c60c8039806?auto=format&fit=crop&w=1920&q=80" alt="" className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#070B14]/40 via-[#070B14]/60 to-[#070B14]" />
        </motion.div>
        <motion.div className="relative z-10 max-w-4xl mx-auto px-6 text-center" style={{ opacity: heroOpacity }}>
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.8 }} className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05] mb-8">
            Scientific Efficiency.<br />Maximal Progress.<br /><span className="text-blue-400">Simplified to Fit Your Life.</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7 }} className="text-white/60 text-base sm:text-lg max-w-2xl mx-auto mb-4 leading-relaxed">
            The most advanced training science. The simplest system to follow.
          </motion.p>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9 }} className="text-white/40 text-sm sm:text-base max-w-2xl mx-auto mb-12 leading-relaxed">
            Most workouts are designed to make you tired. We stripped out everything that doesn't make you stronger — what's left is a system that actually works.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1 }} className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button onClick={() => setShowQuiz(true)} className="bg-blue-600 hover:bg-blue-500 text-white font-semibold px-8 py-3.5 rounded-xl transition-colors text-sm tracking-wide flex items-center gap-2" data-testid="hero-cta">
              Get Started <ChevronRight className="w-4 h-4" />
            </button>
            <button onClick={() => document.getElementById("tension")?.scrollIntoView({ behavior: "smooth" })} className="text-white/40 hover:text-white/70 text-sm transition-colors flex items-center gap-2">
              Keep reading <ArrowRight className="w-4 h-4" />
            </button>
          </motion.div>
        </motion.div>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.8 }} className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10">
          <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.5 }} className="w-6 h-10 border-2 border-white/20 rounded-full flex items-start justify-center pt-2">
            <div className="w-1 h-2 bg-white/40 rounded-full" />
          </motion.div>
        </motion.div>
      </section>

      {/* ── Credentials anchor — surfaces trust before the narrative starts ── */}
      <section className="border-y border-white/5 bg-[#0A0E18]/60 py-14">
        <div className="max-w-5xl mx-auto px-6">
          <FadeIn>
            <p className="text-[10px] tracking-[0.3em] uppercase text-blue-400 font-bold mb-4 text-center">Built on real science, real coaching</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-10 text-center">
              <div>
                <p className="text-3xl sm:text-4xl font-black text-white tracking-tight">25</p>
                <p className="text-[10px] sm:text-xs text-white/50 uppercase tracking-widest mt-1">Years of study</p>
              </div>
              <div>
                <p className="text-3xl sm:text-4xl font-black text-blue-400 tracking-tight">22</p>
                <p className="text-[10px] sm:text-xs text-white/50 uppercase tracking-widest mt-1">Years coaching</p>
              </div>
              <div>
                <p className="text-3xl sm:text-4xl font-black text-emerald-400 tracking-tight">5S</p>
                <p className="text-[10px] sm:text-xs text-white/50 uppercase tracking-widest mt-1">Biomotor framework</p>
              </div>
              <div>
                <p className="text-3xl sm:text-4xl font-black text-amber-400 tracking-tight">0</p>
                <p className="text-[10px] sm:text-xs text-white/50 uppercase tracking-widest mt-1">Junk reps</p>
              </div>
            </div>
            <p className="text-white/40 text-xs sm:text-sm text-center mt-8 max-w-2xl mx-auto leading-relaxed">
              Long-Term Athletic Development science — Balyi, Bompa, Verkhoshansky — distilled into a system that's followed athletes from youth all the way to college.
            </p>
            <div className="text-center mt-6">
              <button onClick={() => navigate("/coach-sachin")} className="text-blue-400 hover:text-blue-300 text-xs font-semibold tracking-wide inline-flex items-center gap-1.5 transition-colors" data-testid="meet-coach-link">
                Meet Coach Sachin <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Tension — The problem, stated with empathy */}
      <section id="tension" className="max-w-3xl mx-auto px-6 py-28">
        <FadeIn>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight leading-snug mb-8">
            Here's what nobody tells you about training:
          </h2>
          <div className="space-y-6 text-white/60 text-base sm:text-lg leading-relaxed">
            <p>Most programs aren't programs. They're lists of exercises. And most people don't need more exercises — they need a <span className="text-white font-semibold">reason behind every rep.</span></p>
            <p>You've felt it. You show up, you do the work, you leave. But something is missing. You're busy, not progressing. Sweating, not improving. Checking boxes instead of building something.</p>
            <p className="text-white/80 text-xl font-semibold">The problem was never your effort.<br />It was always the plan.</p>
          </div>
        </FadeIn>
      </section>

      {/* The Shift — Identity & belonging */}
      <section className="bg-[#0A0E18] py-28">
        <div className="max-w-3xl mx-auto px-6">
          <FadeIn>
            <p className="text-xs tracking-[0.3em] uppercase text-blue-400 font-semibold mb-6">A different approach</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight leading-snug mb-8">
              What if your training actually<br /><span className="text-blue-400">made sense?</span>
            </h2>
            <div className="space-y-6 text-white/60 text-base sm:text-lg leading-relaxed">
              <p>Paladin doesn't give you random workouts. We give you a system — one built on how your body actually works. How it produces force. How it stores energy. How it expresses power.</p>
              <p>Every exercise has a reason. Every phase builds on the last. And every week, you're not just working out — you're <span className="text-white font-semibold">building something that compounds.</span></p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* The Method — The 5S framework, the actual system */}
      <section id="method" className="max-w-6xl mx-auto px-6 py-28">
        <FadeIn>
          <p className="text-xs tracking-[0.3em] uppercase text-white/30 font-semibold mb-3">The system</p>
          <h2 className="text-2xl sm:text-3xl font-black tracking-tight mb-3">
            Five biomotor abilities. One coherent plan.
          </h2>
          <p className="text-white/40 text-sm mb-12 max-w-2xl leading-relaxed">
            Long-Term Athletic Development isn't a buzzword. It's a 40-year body of research from Balyi, Bompa, and Verkhoshansky. Every program SWOTs the athlete against the demands of their sport — then trains the abilities that move the needle.
          </p>
        </FadeIn>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {[
            { title: "SPEED", desc: "How fast you can move force. The first thing to lose with bad programming, the hardest to rebuild.", color: "text-amber-400", bg: "bg-amber-500/10", border: "border-amber-500/20" },
            { title: "STRENGTH", desc: "The base. Force production. Without it, every other quality is theoretical.", color: "text-red-400", bg: "bg-red-500/10", border: "border-red-500/20" },
            { title: "STAMINA", desc: "Your engine. Specific to your sport — long-game endurance or short-burst recovery.", color: "text-blue-400", bg: "bg-blue-500/10", border: "border-blue-500/20" },
            { title: "SUPPLENESS", desc: "Mobility through the full range. Where injuries either hide or get prevented.", color: "text-emerald-400", bg: "bg-emerald-500/10", border: "border-emerald-500/20" },
            { title: "SKILL", desc: "Where the other four become a sprint, a jump, a change of direction. The point of all of it.", color: "text-violet-400", bg: "bg-violet-500/10", border: "border-violet-500/20" },
          ].map((pillar, i) => (
            <FadeIn key={i} delay={i * 0.08}>
              <div className={`${pillar.bg} border ${pillar.border} rounded-2xl p-5 h-full`}>
                <h3 className={`text-sm font-black tracking-wider mb-3 ${pillar.color}`}>{pillar.title}</h3>
                <p className="text-white/50 text-xs leading-relaxed">{pillar.desc}</p>
              </div>
            </FadeIn>
          ))}
        </div>
      </section>

      {/* No junk reps — the methodology, told as a promise */}
      <section className="bg-[#0A0E18] py-28">
        <div className="max-w-3xl mx-auto px-6">
          <FadeIn>
            <p className="text-xs tracking-[0.3em] uppercase text-amber-400 font-semibold mb-6">Zero junk reps</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight leading-snug mb-8">
              Every set has a reason.<br /><span className="text-white/60">If it doesn't, we cut it.</span>
            </h2>
            <div className="space-y-6 text-white/60 text-base sm:text-lg leading-relaxed">
              <p>Most programs add volume because volume sounds productive. We do the opposite. Every rep, every set, every rest interval is set up for one reason: <span className="text-white font-semibold">optimal adaptation with the lowest possible cost.</span></p>
              <p>As you get stronger relative to your bodyweight, you don't need more reps — you need <span className="text-white font-semibold">smarter reps.</span> Our auto-progression engine adjusts loads and volumes automatically based on what you actually did last session. No spreadsheets. No guessing. No wasted CNS.</p>
              <p className="text-white/80 text-xl font-semibold pt-2">More signal. Less noise. Less injury. More PRs.</p>
            </div>
            <button onClick={() => navigate("/coach-sachin")} className="mt-8 text-blue-400 hover:text-blue-300 text-sm font-semibold inline-flex items-center gap-1.5 transition-colors" data-testid="why-paladin-link">
              How the system works <ArrowRight className="w-4 h-4" />
            </button>
          </FadeIn>
        </div>
      </section>

      {/* The Invitation — Not "who we serve" but "who you are" */}
      <section className="relative py-28 overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1696536823512-79d724454616?auto=format&fit=crop&w=1920&q=80" alt="" className="w-full h-full object-cover opacity-8" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#070B14] via-[#070B14]/95 to-[#070B14]" />
        </div>
        <div className="relative z-10 max-w-3xl mx-auto px-6">
          <FadeIn>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight leading-snug mb-8">
              People like you<br />do things like this.
            </h2>
            <div className="space-y-6 text-white/60 text-base sm:text-lg leading-relaxed">
              <p>Maybe you're <span className="text-white font-semibold">14 and trying to earn a starting spot.</span> Maybe you're <span className="text-white font-semibold">38 with a squat rack in your garage</span> and three hours a week to make something happen.</p>
              <p>Maybe you played in college and you miss what it felt like to train with purpose. Maybe you've never picked up a barbell and you just know it's time.</p>
              <p className="text-white/80 font-semibold">It doesn't matter where you start. What matters is that the system meets you there.</p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Paths — presented as natural next steps */}
      <section className="max-w-6xl mx-auto px-6 py-28">
        <FadeIn>
          <p className="text-xs tracking-[0.3em] uppercase text-blue-400 font-semibold mb-3">Your path</p>
          <h2 className="text-2xl sm:text-3xl font-black tracking-tight mb-4">One system. Built around your life.</h2>
          <p className="text-white/40 text-sm mb-12 max-w-xl">Each path uses the same science. The difference is where you are and how you train.</p>
        </FadeIn>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {[
            { title: "Youth Athletes — Foundations", desc: "Build the base before the competition demands it.", path: "/foundations/youth", color: "text-blue-400", border: "border-blue-500/20", bg: "hover:bg-blue-500/5" },
            { title: "Adults — Foundations", desc: "Start where you are. Build what lasts.", path: "/foundations/adults", color: "text-blue-400", border: "border-blue-500/20", bg: "hover:bg-blue-500/5" },
            { title: "Home Gym — Accelerator", desc: "You bought the equipment. We'll bring the plan.", path: "/accelerator/home-gym", color: "text-emerald-400", border: "border-emerald-500/20", bg: "hover:bg-emerald-500/5" },
            { title: "Sports Performance — Accelerator", desc: "For athletes who are done guessing.", path: "/accelerator/sports-performance", color: "text-rose-400", border: "border-rose-500/20", bg: "hover:bg-rose-500/5" },
          ].map((card, i) => (
            <FadeIn key={i} delay={i * 0.08}>
              <button onClick={() => navigate(card.path)} className={`w-full text-left bg-[#0f172a] border ${card.border} rounded-2xl p-6 ${card.bg} transition-all duration-300 group`}>
                <h3 className={`text-base font-bold ${card.color} mb-1`}>{card.title}</h3>
                <p className="text-white/50 text-sm mb-4">{card.desc}</p>
                <span className={`text-xs font-semibold ${card.color} flex items-center gap-1.5 group-hover:gap-2.5 transition-all`}>
                  Learn more <ChevronRight className="w-3.5 h-3.5" />
                </span>
              </button>
            </FadeIn>
          ))}
        </div>
      </section>

      {/* Social Proof — Stories, not stats */}
      <section className="bg-[#0A0E18] py-24">
        <div className="max-w-6xl mx-auto px-6">
          <FadeIn>
            <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-center mb-12">They were where you are.</h2>
          </FadeIn>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { quote: "I've been in weight rooms my whole life. Paladin is the first program that actually made sense of why I was doing what I was doing. That changed everything.", name: "D1 Volleyball Athlete" },
              { quote: "My son's vertical went up 4 inches in 3 months. But more than that — he understands his body now. He trains with intention.", name: "Parent of 10th Grader" },
              { quote: "I was doing random workouts in my garage for two years. Same weights, same routine. Paladin gave me a plan and I haven't plateaued since.", name: "Home Gym Athlete" },
            ].map((t, i) => (
              <FadeIn key={i} delay={i * 0.1}>
                <div className="bg-[#0f172a] border border-[#1E293B] rounded-2xl p-6 h-full flex flex-col">
                  <p className="text-white/70 text-sm italic leading-relaxed flex-1">"{t.quote}"</p>
                  <p className="text-blue-400 text-xs font-semibold mt-4">{t.name}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA — The invitation */}
      <section className="max-w-3xl mx-auto px-6 py-28 text-center">
        <FadeIn>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-tight mb-6">
            The best time to start<br />was yesterday.<br /><span className="text-blue-400">The second best time<br />is right now.</span>
          </h2>
          <p className="text-white/50 text-base max-w-lg mx-auto mb-10 leading-relaxed">
            You don't need to know everything. You just need to show up with intent. We'll handle the rest.
          </p>
          <button onClick={() => setShowQuiz(true)} className="bg-blue-600 hover:bg-blue-500 text-white font-semibold px-10 py-4 rounded-xl transition-colors text-base tracking-wide" data-testid="final-cta">
            Find Your Program
          </button>
        </FadeIn>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 py-8">
        <div className="max-w-6xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <img src="/paladin-logo.jpeg" alt="" className="w-7 h-7 rounded object-contain" />
            <span className="text-white/30 text-xs tracking-wider font-bold">PALADIN HHP</span>
          </div>
          <div className="flex items-center gap-6 text-white/30 text-xs">
            <button onClick={() => navigate("/legal/terms")} className="hover:text-white/60 transition-colors">Terms</button>
            <button onClick={() => navigate("/legal/privacy")} className="hover:text-white/60 transition-colors">Privacy</button>
            <button onClick={() => navigate("/login")} className="hover:text-white/60 transition-colors">Sign In</button>
          </div>
        </div>
      </footer>

      {/* Quiz Popup */}
      {showQuiz && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/85 backdrop-blur-sm">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="w-full max-w-2xl bg-[#0A0F1A] border border-white/10 rounded-2xl overflow-hidden">
            <div className="p-6 border-b border-white/10 flex items-center justify-between">
              <h2 className="text-lg font-bold text-white">Find Your Program</h2>
              <button onClick={() => { setShowQuiz(false); setQuizStep(0); }} className="p-1.5 text-white/40 hover:text-white hover:bg-white/10 rounded-lg transition-colors" data-testid="close-quiz">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              {quizStep === 0 && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                  <p className="text-white/50 text-sm mb-6">Where are you on your journey?</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <button onClick={() => setQuizStep(1)} className="group bg-[#0f172a] border border-blue-500/20 hover:border-blue-400/50 rounded-xl p-5 text-left transition-all" data-testid="quiz-beginner">
                      <Dumbbell className="w-7 h-7 text-blue-400 mb-3" />
                      <h3 className="font-bold text-white mb-1">I'm Just Starting</h3>
                      <p className="text-white/50 text-xs">Building a base or getting back into it</p>
                    </button>
                    <button onClick={() => setQuizStep(2)} className="group bg-[#0f172a] border border-amber-500/20 hover:border-amber-400/50 rounded-xl p-5 text-left transition-all" data-testid="quiz-advanced">
                      <Zap className="w-7 h-7 text-amber-400 mb-3" />
                      <h3 className="font-bold text-white mb-1">I'm Ready for More</h3>
                      <p className="text-white/50 text-xs">I have experience and want elite programming</p>
                    </button>
                  </div>
                </motion.div>
              )}
              {quizStep === 1 && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                  <button onClick={() => setQuizStep(0)} className="text-white/40 hover:text-white/70 text-xs mb-4 flex items-center gap-1">
                    <ChevronRight className="w-3 h-3 rotate-180" /> Back
                  </button>
                  <p className="text-white/50 text-sm mb-6">Which sounds more like you?</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <button onClick={() => { setShowQuiz(false); setQuizStep(0); navigate("/foundations/youth"); }} className="group bg-[#0f172a] border border-blue-500/20 hover:border-blue-400/50 rounded-xl p-5 text-left transition-all" data-testid="quiz-youth">
                      <Users className="w-7 h-7 text-blue-400 mb-3" />
                      <h3 className="font-bold text-white mb-1">Youth Athlete</h3>
                      <p className="text-white/50 text-xs">Ages 10-18, building a foundation</p>
                    </button>
                    <button onClick={() => { setShowQuiz(false); setQuizStep(0); navigate("/foundations/adults"); }} className="group bg-[#0f172a] border border-blue-500/20 hover:border-blue-400/50 rounded-xl p-5 text-left transition-all" data-testid="quiz-adult">
                      <User className="w-7 h-7 text-blue-400 mb-3" />
                      <h3 className="font-bold text-white mb-1">Adult</h3>
                      <p className="text-white/50 text-xs">Getting started or getting back into it</p>
                    </button>
                  </div>
                </motion.div>
              )}
              {quizStep === 2 && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                  <button onClick={() => setQuizStep(0)} className="text-white/40 hover:text-white/70 text-xs mb-4 flex items-center gap-1">
                    <ChevronRight className="w-3 h-3 rotate-180" /> Back
                  </button>
                  <p className="text-white/50 text-sm mb-6">Where do you train?</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <button onClick={() => { setShowQuiz(false); setQuizStep(0); navigate("/accelerator/home-gym"); }} className="group bg-[#0f172a] border border-emerald-500/20 hover:border-emerald-400/50 rounded-xl p-5 text-left transition-all" data-testid="quiz-homegym">
                      <Home className="w-7 h-7 text-emerald-400 mb-3" />
                      <h3 className="font-bold text-white mb-1">Home Gym</h3>
                      <p className="text-white/50 text-xs">Rack, barbell, dumbbells — your space</p>
                    </button>
                    <button onClick={() => { setShowQuiz(false); setQuizStep(0); navigate("/accelerator/sports-performance"); }} className="group bg-[#0f172a] border border-rose-500/20 hover:border-rose-400/50 rounded-xl p-5 text-left transition-all" data-testid="quiz-sports">
                      <Trophy className="w-7 h-7 text-rose-400 mb-3" />
                      <h3 className="font-bold text-white mb-1">Sports Performance</h3>
                      <p className="text-white/50 text-xs">Speed, power, competitive edge</p>
                    </button>
                  </div>
                </motion.div>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default SplashPage;
